import { useQueries, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  getAdHocReportStatus,
  getGetAdHocReportStatusQueryKey,
  getGetChatQueryKey,
  useGetChat,
} from "~/api/chat/chat";
import { useGetPageTextContent } from "~/api/documents/documents";
import { useGetKnowledgebasePageContent } from "~/api/knowledge/knowledge";
import { Chat, ChatMessage, SourceReference } from "~/api/model";
import { useGetCurrentUser } from "~/api/users/users";
import skyLogoIcon from "~/assets/LogoSymbol.png";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { HighlightedMarkdown } from "~/components/HighlightedMarkdown";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { MarkdownExportButton } from "~/components/MarkdownExportButton";
import { TabControlBar } from "~/components/TabControlBar";
import {
  buildTermMap,
  TermDefinitionDialog,
} from "~/components/TermDefinitionDialog";
import { TTSButton } from "~/components/TTSButton";
import { Button } from "~/components/ui/button";
import { Bubble, BubbleContent } from "~/components/ui/chat/bubble";
import { ChatInput } from "~/components/ui/chat/chat-input";
import {
  type ChatImageAttachment,
  chatImageDataUrl,
} from "~/components/ui/chat/imageAttachment";
import { Message, MessageContent } from "~/components/ui/chat/message";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from "~/components/ui/chat/message-scroller";
import { UnifiedMarkdown } from "~/components/UnifiedMarkdown";
import { useSSEChatMessage } from "~/hooks/useSSEChatMessage";
import { trackEvent } from "~/utils/analytics";
import { getMedicationMatcher } from "~/utils/termMatcher";

import Loader from "~/assets/Loader.svg";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { useDocList } from "~/features/chat/hooks/useDocList";
import { useMergedTerms } from "~/features/chat/hooks/useMergedTerms";
import { useMedicationsStore } from "~/features/chat/stores/medicationsStore";
import { useSkyViewStore } from "~/features/skyView/store";
import {
  formatDate,
  generateChatMarkdownContent,
  ROUTES,
  useChatDocumentParams,
} from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import {
  type SectionReferenceScope,
  useSourceMapDelegation,
} from "~/utils/markdownTerms";
import { rehypeTtsHighlight } from "~/utils/rehypeTtsHighlight";
import {
  formatCaseInfo,
  getFileTitle,
  injectBatesLabels,
  normalizeCitationMarkers,
  stripReferenceLinksFromContent,
  stripSourcesFromContent,
  stripStructuredDataTrailer,
} from "../helpers";
import { AsyncReportBlock } from "./AsyncReportBlock";
import { ChatDisclaimerBanner } from "./ChatDisclaimerBanner";
import { ChatHistorySelector } from "./ChatHistorySelector";
import { ClearChatButton } from "./ClearChat";
import { ImageLightbox } from "./ImageLightbox";
import { PromptTips } from "./PromptTips";
import { rehypeGroupSourceCitations } from "./reportArea/rehypeGroupSourceCitations";
import { ReportContainerContext } from "./reportArea/reportContainerContext";
import { reportCitationComponents } from "./reportArea/ReportSourceCitationGroup";
import { useReportTerms } from "./reportArea/useReportTerms";
import { SuggestQuestions } from "./SuggestQuestions";

export const ChatArea: FC = () => {
  const navigate = useNavigate();
  const { organizationId, caseId, chatId, documentId, isGuest } =
    useChatDocumentParams();
  const {
    data: chat,
    isLoading,
    error,
  } = useGetChat(organizationId, caseId, chatId);
  const { data: currentUser } = useGetCurrentUser();
  const { project } = useParentCase();
  const { docList, pageItems, absolutePageNumbers, firstPageIds } =
    useDocList();
  const [, updateDocumentParams] = useDocumentSearchParams();
  const { formatMessage } = useIntl();
  const { isMobile } = useMobile();
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    if (
      error &&
      (error as unknown as AxiosError)?.response?.status === 403 &&
      currentUser?.guestInOrganizations?.includes(organizationId)
    ) {
      navigate(ROUTES.ACCESS_DENIED);
    }
  }, [error, currentUser, navigate, organizationId]);

  const caseInfo = project ? formatCaseInfo(project) : undefined;

  // For single-document chats, hide sources since they come from the entire case
  const isSingleDocumentChat = !!documentId;

  const combinedAiTerms = useReportTerms(caseId);
  const [readingMap, setReadingMap] = useState<
    Record<string, { reading: boolean; idx: number | null; len: number }>
  >({});
  const setMsgState = (
    id: string,
    next: { reading: boolean; idx: number | null; len: number }
  ) => setReadingMap((m) => ({ ...m, [id]: next }));
  const getMsgState = (id: string) =>
    readingMap[id] ?? { reading: false, idx: null, len: 0 };

  const [pageTextModalOpen, setPageTextModalOpen] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [pageImageUrl, setPageImageUrl] = useState<string | undefined>(
    undefined
  );
  const [pageRotation, setPageRotation] = useState(0);

  // Knowledge base source state
  const [selectedKbDocumentId, setSelectedKbDocumentId] = useState<
    string | null
  >(null);
  const [selectedKbPageNumber, setSelectedKbPageNumber] = useState<
    string | null
  >(null);
  const [isKnowledgeBaseSource, setIsKnowledgeBaseSource] = useState(false);
  const [feedbackById, setFeedbackById] = useState<
    Record<string, "up" | "down" | null>
  >({});

  const queryClient = useQueryClient();

  // Fetch case document page content
  const {
    data: pageContentData,
    isLoading: isPageContentLoading,
    error: pageContentError,
  } = useGetPageTextContent(
    organizationId,
    project?.id || "",
    selectedPageId ?? "",
    {
      query: {
        enabled:
          !isKnowledgeBaseSource &&
          !!selectedPageId &&
          !!project?.id &&
          selectedPageId !== "unknown" &&
          organizationId !== "unknown",
      },
    }
  );

  // Fetch knowledge base document page content
  const {
    data: kbPageContentData,
    isLoading: isKbPageContentLoading,
    error: kbPageContentError,
  } = useGetKnowledgebasePageContent(
    organizationId,
    selectedKbDocumentId ?? "",
    selectedKbPageNumber ?? "",
    {
      query: {
        enabled:
          isKnowledgeBaseSource &&
          !!selectedKbDocumentId &&
          !!selectedKbPageNumber &&
          !!selectedKbPageNumber &&
          organizationId !== "unknown",
      },
    }
  );

  // SSE streaming chat functionality
  const handleSSEMessageEnd = useCallback(
    (event: {
      content: string;
      sources?: SourceReference[];
      messageId: string;
    }) => {
      const assistantMessage: ChatMessage = {
        id: event.messageId,
        role: "assistant",
        content: event.content,
        timestamp: Date.now(),
        sources: event.sources,
      };

      queryClient.setQueryData(
        getGetChatQueryKey(organizationId, caseId, chatId),
        (oldData: Chat | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            messages: [...(oldData.messages || []), assistantMessage],
          };
        }
      );
    },
    [queryClient, organizationId, caseId, chatId]
  );

  const handleAsyncReportScheduled = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getGetChatQueryKey(organizationId, caseId, chatId),
    });
  }, [queryClient, organizationId, caseId, chatId]);

  const {
    sendMessage: sendSSEMessage,
    cancelStream: _cancelStream,
    isStreaming,
    isThinking,
    isComprehensiveProcessing,
    comprehensiveStage,
    isFetchingLinks,
    linkFetchUrls,
    streamingContent,
    error: sseError,
  } = useSSEChatMessage({
    onMessageEnd: handleSSEMessageEnd,
    onAsyncReportScheduled: handleAsyncReportScheduled,
  });
  void _cancelStream;

  const handleFeedbackClick = (messageId: string, vote: "up" | "down") => {
    const previousFeedback = feedbackById[messageId];
    const newFeedback = previousFeedback === vote ? null : vote;

    setFeedbackById((prev) => ({
      ...prev,
      [messageId]: newFeedback,
    }));

    trackEvent({
      eventName: "chat_response_feedback",
      properties: {
        vote: newFeedback,
        chat_id: chatId,
        message_id: messageId,
      },
    });
  };

  const asyncReportMessages = useMemo(
    () =>
      (chat?.messages ?? []).filter(
        (
          message
        ): message is ChatMessage & {
          jobId: string;
          type: "async_report";
        } => message.type === "async_report" && !!message.jobId
      ),
    [chat?.messages]
  );

  const asyncReportQueries = useQueries({
    queries: asyncReportMessages.map((message) => ({
      queryKey: getGetAdHocReportStatusQueryKey(
        organizationId,
        caseId,
        chatId,
        message.jobId
      ),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getAdHocReportStatus(
          organizationId,
          caseId,
          chatId,
          message.jobId,
          undefined,
          signal
        ),
      enabled: !!organizationId && !!caseId && !!chatId && !!message.jobId,
      staleTime: Infinity,
      refetchInterval: (query: any) => {
        if (query.state.status === "error") return false;
        if (
          query.state.data?.status === "completed" ||
          query.state.data?.status === "failed"
        ) {
          return false;
        }
        return 3000;
      },
      refetchIntervalInBackground: true,
    })),
  });

  const allTerms = useMergedTerms(combinedAiTerms);

  const medications = useMedicationsStore((s) => s.medications);
  const medicationMatcher = useMemo(
    () => getMedicationMatcher(medications),
    [medications]
  );
  const termMap = useMemo(
    () => buildTermMap(combinedAiTerms, medications),
    [combinedAiTerms, medications]
  );

  const handleSendMessage = async (
    message: string,
    attachment?: ChatImageAttachment | null
  ) => {
    trackEvent({ eventName: "app/doc_list/chat_input.input/click" });
    const userMessage: ChatMessage = {
      id: String(Date.now()),
      role: "user",
      content: message,
      timestamp: Date.now(),
      ...(attachment
        ? { image: attachment.base64, imageMimeType: attachment.mimeType }
        : {}),
    };
    const updatedMessages = (chat?.messages || []).concat(userMessage);
    queryClient.setQueryData(
      getGetChatQueryKey(organizationId, caseId, chatId),
      {
        ...chat,
        messages: updatedMessages,
      }
    );

    sendSSEMessage(
      message,
      attachment
        ? { image: attachment.base64, imageMimeType: attachment.mimeType }
        : null
    );
  };

  const isChatEmpty = isLoading || chat?.messages.length === 0;
  const isMessageLoading = isStreaming;
  const chatError = sseError;

  const [chatWrapperEl, setChatWrapperEl] = useState<HTMLDivElement | null>(
    null
  );

  // Handle page reference clicks
  const handlePageReferenceClick = useCallback(
    async (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains("page-reference")) {
        event.preventDefault();

        const pageId = target.dataset.pageId;
        const pageOrgId = target.dataset.organizationId;
        const documentId = target.dataset.documentId;
        let isKbSource = !pageItems.find((p) => p.pageId === pageId);

        const batesNumber = target.dataset.batesNumber;

        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: {
            hasBatesNumber: !!batesNumber,
            sourceType: isKbSource ? "kb" : "document",
          },
        });

        // Handle standalone Bates citation (no pageId/organizationId)
        if (batesNumber && (!pageId || !pageOrgId)) {
          // Search for page by Bates number in loaded pages
          const normalize = (s: string) =>
            s.replace(/[\r\n]+/g, " ").toLowerCase();
          const pageItem = pageItems.find(
            (p) =>
              p.batesNumber &&
              normalize(p.batesNumber) === normalize(batesNumber)
          );

          if (pageItem) {
            setIsKnowledgeBaseSource(false);
            setSelectedKbDocumentId(null);
            setSelectedKbPageNumber(null);
            setSelectedPageId(pageItem.pageId);
            setIsContentLoading(true);
            setPageTextModalOpen(true);
            setPageRotation(pageItem.rotate);

            try {
              const urls = await pageItem.getUrls();
              setPageImageUrl(urls.retinaUrl || urls.highResUrl || undefined);
            } catch (error) {
              setPageImageUrl(undefined);
            } finally {
              setIsContentLoading(false);
            }
          }
          return;
        }

        // Check for unknown IDs
        if (
          pageId === "unknown" ||
          pageOrgId === "unknown" ||
          !pageId ||
          !pageOrgId
        ) {
          return;
        }

        if (isKbSource) {
          if (pageId && documentId) {
            setIsKnowledgeBaseSource(true);
            setSelectedKbDocumentId(documentId);
            setSelectedKbPageNumber(pageId);
            setSelectedPageId(null);
            setPageImageUrl(undefined);
            setPageRotation(0);
            setIsContentLoading(false);
            setPageTextModalOpen(true);
          }
        } else {
          // Case document source - pageId is the page UUID
          setIsKnowledgeBaseSource(false);
          setSelectedKbDocumentId(null);
          setSelectedKbPageNumber(null);
          setSelectedPageId(pageId);
          setIsContentLoading(true);
          setPageTextModalOpen(true);

          const pageItem = pageItems.find((p) => p.pageId === pageId);
          if (pageItem) {
            setPageRotation(pageItem.rotate);

            try {
              const urls = await pageItem.getUrls();
              setPageImageUrl(urls.retinaUrl || urls.highResUrl || undefined);
            } catch (error) {
              setPageImageUrl(undefined);
            } finally {
              setIsContentLoading(false);
            }
          } else {
            setIsContentLoading(false);
          }
        }
      }
    },
    [pageItems]
  );

  // Add event listener for page reference clicks
  useEffect(() => {
    document.addEventListener("click", handlePageReferenceClick);
    return () => {
      document.removeEventListener("click", handlePageReferenceClick);
    };
  }, [handlePageReferenceClick]);

  const { chatMarkdownContent, chatRichContent, exportFileName } =
    useMemo(() => {
      const asyncReportContentByJobId = new Map(
        asyncReportMessages.map((message, index) => {
          const status = asyncReportQueries[index]?.data;
          const content =
            status?.status === "completed"
              ? status.result.content
              : message.content;

          return [message.jobId, content];
        })
      );

      const PDF_SAFE_MIME_TYPES = ["image/png", "image/jpeg"];

      const plainMessages = (chat?.messages || []).map((msg) => {
        const content =
          msg.role === "assistant" && msg.type === "async_report" && msg.jobId
            ? (asyncReportContentByJobId.get(msg.jobId) ?? msg.content)
            : msg.content;

        const imagePlaceholder = msg.image
          ? formatMessage({
              id: "chat.attachment.exportPlaceholder",
              defaultMessage: "[Attached image]",
            })
          : "";
        const exportContent =
          msg.role === "assistant"
            ? stripReferenceLinksFromContent(stripSourcesFromContent(content))
            : [content, imagePlaceholder].filter(Boolean).join("\n\n");

        return { ...msg, content: exportContent };
      });

      const richMessages = (chat?.messages || []).map((msg) => {
        const content =
          msg.role === "assistant" && msg.type === "async_report" && msg.jobId
            ? (asyncReportContentByJobId.get(msg.jobId) ?? msg.content)
            : msg.content;

        const imageMarkdown =
          msg.image &&
          msg.imageMimeType &&
          PDF_SAFE_MIME_TYPES.includes(msg.imageMimeType)
            ? `![${formatMessage({ id: "chat.attachment.alt", defaultMessage: "Attached image" })}](${chatImageDataUrl(msg.image, msg.imageMimeType)})`
            : msg.image
              ? formatMessage({
                  id: "chat.attachment.exportPlaceholder",
                  defaultMessage: "[Attached image]",
                })
              : "";
        const exportContent =
          msg.role === "assistant"
            ? stripReferenceLinksFromContent(stripSourcesFromContent(content))
            : [content, imageMarkdown].filter(Boolean).join("\n\n");

        return { ...msg, content: exportContent };
      });

      const chatMarkdownContent = generateChatMarkdownContent({
        title: chat?.title ?? "Unknown",
        messages: plainMessages,
        date: new Date().toISOString(),
      });

      const chatRichContent = generateChatMarkdownContent({
        title: chat?.title ?? "Unknown",
        messages: richMessages,
        date: new Date().toISOString(),
      });

      const t = getFileTitle(chat?.title ?? "Unknown");
      const exportFileName = `${formatMessage({ id: `chat.export.filePrefix` })} "${t}" (${formatDate(new Date().toISOString())})`;

      return {
        chatMarkdownContent,
        chatRichContent,
        exportFileName,
      };
    }, [
      asyncReportMessages,
      asyncReportQueries,
      chat?.title,
      chat?.messages,
      formatMessage,
    ]);

  const highlightReference = useSkyViewStore(
    (state) => state.highlightReference
  );

  const handleSectionClick = useCallback(
    (
      sectionId: string,
      pageId: string,
      context: string,
      scope: SectionReferenceScope
    ) => {
      const resolvedPageId = pageId || firstPageIds[sectionId] || "";
      updateDocumentParams({
        sectionId: sectionId || undefined,
        pageId: resolvedPageId,
        tab: "chat",
      });
      if (resolvedPageId && context)
        highlightReference(
          resolvedPageId,
          context,
          scope === "page" ? null : sectionId
        );
    },
    [updateDocumentParams, highlightReference, firstPageIds]
  );

  const handleBatesClick = useCallback(
    (pageId: string, context?: string) => {
      updateDocumentParams({ pageId });
      if (context) highlightReference(pageId, context);
    },
    [updateDocumentParams, highlightReference]
  );

  useSourceMapDelegation(chatWrapperEl, handleSectionClick, handleBatesClick);

  const modalTitle = useMemo(() => {
    if (!selectedPageId) return "Page Content Recognition";

    const pageItem = pageItems.find((p) => p.pageId === selectedPageId);
    if (!pageItem) return "Page Content Recognition";

    const section = docList?.tree
      .flatMap((doc) => doc.sections)
      .find((sec) => sec.id === pageItem.sectionId);

    const sectionName = section?.name || "Unknown Section";
    const pageNumber = absolutePageNumbers[selectedPageId] || 0;

    return `${sectionName} – Page ${pageNumber}`;
  }, [selectedPageId, pageItems, docList, absolutePageNumbers]);

  useEffect(() => {
    if (!pageTextModalOpen) {
      setPageImageUrl(undefined);
      setPageRotation(0);
    }
  }, [pageTextModalOpen]);

  return (
    <div
      className={`bg-background flex flex-col ${isMobile ? "h-docsidebar-content-mobile" : "h-[calc(100vh-60px)]"}`}
    >
      {!isGuest && <ChatHistorySelector />}
      <ChatDisclaimerBanner />
      <MessageScrollerProvider key={chatId} autoScroll>
        <MessageScroller className="flex-1 min-h-0">
          <MessageScrollerViewport
            ref={setChatWrapperEl}
            className="chat-wrapper"
          >
            <ReportContainerContext.Provider value={chatWrapperEl}>
              <TermDefinitionDialog
                containerRef={chatWrapperEl}
                termMap={termMap}
              />
              <MessageScrollerContent className="min-h-full px-3 pt-3 pb-4 gap-4">
                {isChatEmpty ? (
                  <>
                    <div className="p-4 grow flex flex-col justify-center items-center gap-3">
                      <div className="bg-background size-[64px] rounded-full flex justify-center items-center relative">
                        <img
                          src={skyLogoIcon}
                          alt="Chat"
                          className="h-[42px]"
                        />
                      </div>
                      <span className="text-foreground text-sm font-normal">
                        <FormattedMessage id="chat.welcomeMessage" />
                      </span>
                    </div>
                    <PromptTips
                      onTipClick={(doText) => handleSendMessage(doText)}
                    />
                    <SuggestQuestions
                      onSuggestionClick={({ value }) =>
                        handleSendMessage(value)
                      }
                    />
                  </>
                ) : (
                  <MessageScrollerItem>
                    <SuggestQuestions
                      onSuggestionClick={({ value }) =>
                        handleSendMessage(value)
                      }
                    />
                  </MessageScrollerItem>
                )}
                {chat?.messages.map((message) => {
                  if (message.type === "async_report" && message.jobId) {
                    return (
                      <MessageScrollerItem
                        key={message.id}
                        messageId={message.id}
                      >
                        <Message align="start">
                          <MessageContent>
                            <AsyncReportBlock
                              jobId={message.jobId}
                              terms={combinedAiTerms}
                              medicationMatcher={medicationMatcher}
                            />
                          </MessageContent>
                        </Message>
                      </MessageScrollerItem>
                    );
                  }
                  const isUser = message.role === "user";
                  const markdown = (
                    <HighlightedMarkdown
                      className="leading-5"
                      terms={
                        message.role === "assistant" &&
                        !getMsgState(message.id).reading
                          ? combinedAiTerms
                          : []
                      }
                      medicationMatcher={
                        message.role === "assistant" &&
                        !getMsgState(message.id).reading
                          ? medicationMatcher
                          : null
                      }
                      extraRehypePlugins={
                        message.role === "assistant" &&
                        getMsgState(message.id).reading &&
                        getMsgState(message.id).idx !== null &&
                        getMsgState(message.id).len > 0
                          ? [
                              rehypeTtsHighlight(
                                getMsgState(message.id).idx,
                                getMsgState(message.id).len
                              ),
                            ]
                          : []
                      }
                      extraRehypePluginsAfterRaw={
                        message.role === "assistant"
                          ? [rehypeGroupSourceCitations]
                          : []
                      }
                      components={reportCitationComponents}
                    >
                      {message.role === "assistant" &&
                      isSingleDocumentChat &&
                      getMsgState(message.id).reading
                        ? stripSourcesFromContent(message.content)
                        : injectBatesLabels(message.content)}
                    </HighlightedMarkdown>
                  );
                  return (
                    <MessageScrollerItem
                      key={message.id}
                      messageId={message.id}
                      scrollAnchor={isUser}
                    >
                      <Message align={isUser ? "end" : "start"}>
                        {isUser ? (
                          <Bubble variant="muted" align="end">
                            <BubbleContent>
                              {message.image && message.imageMimeType && (
                                <img
                                  src={chatImageDataUrl(
                                    message.image,
                                    message.imageMimeType
                                  )}
                                  alt={formatMessage({
                                    id: "chat.attachment.alt",
                                    defaultMessage: "Attached image",
                                  })}
                                  className="mb-2 max-h-64 max-w-full cursor-pointer rounded-md object-contain"
                                  onClick={() =>
                                    setLightboxImage(
                                      chatImageDataUrl(
                                        message.image!,
                                        message.imageMimeType!
                                      )
                                    )
                                  }
                                />
                              )}
                              {markdown}
                            </BubbleContent>
                          </Bubble>
                        ) : (
                          <MessageContent>
                            {markdown}
                            <div className="h-6 invisible opacity-0 group-hover/message:visible group-hover/message:opacity-100 transition-opacity duration-200">
                              <div className="inline-flex items-center gap-2">
                                <ContentCopyButton
                                  content={
                                    isSingleDocumentChat
                                      ? stripSourcesFromContent(message.content)
                                      : stripStructuredDataTrailer(
                                          message.content
                                        )
                                  }
                                  disabled={false}
                                  color="muted-foreground"
                                  preCopyMessageId="copy"
                                  eventName="app/doc_list/copy_message.button/click"
                                />
                                <TTSButton
                                  text={
                                    isSingleDocumentChat
                                      ? stripSourcesFromContent(message.content)
                                      : stripStructuredDataTrailer(
                                          message.content
                                        )
                                  }
                                  isLoading={false}
                                  color="muted-foreground"
                                  onStart={() =>
                                    setMsgState(message.id, {
                                      reading: true,
                                      idx: 0,
                                      len: 0,
                                    })
                                  }
                                  onEnd={() =>
                                    setMsgState(message.id, {
                                      reading: false,
                                      idx: null,
                                      len: 0,
                                    })
                                  }
                                  onBoundary={({ charIndex, charLength }) =>
                                    setMsgState(message.id, {
                                      reading: true,
                                      idx: charIndex,
                                      len: charLength,
                                    })
                                  }
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={formatMessage({
                                    id: "chat.feedback.helpful",
                                    defaultMessage: "Mark as Helpful",
                                  })}
                                  className="h-4 w-4 p-0"
                                  onClick={() =>
                                    handleFeedbackClick(message.id, "up")
                                  }
                                  disabled={isMessageLoading}
                                >
                                  <ThumbsUp
                                    className={`h-3 w-3 ${
                                      feedbackById[message.id] === "up"
                                        ? "fill-blue-500 text-blue-500"
                                        : "text-muted-foreground"
                                    }`}
                                  />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={formatMessage({
                                    id: "chat.feedback.notHelpful",
                                    defaultMessage: "Mark as Not Helpful",
                                  })}
                                  className="h-4 w-4 p-0"
                                  onClick={() =>
                                    handleFeedbackClick(message.id, "down")
                                  }
                                  disabled={isMessageLoading}
                                >
                                  <ThumbsDown
                                    className={`h-3 w-3 ${
                                      feedbackById[message.id] === "down"
                                        ? "fill-red-500 text-red-500"
                                        : "text-muted-foreground"
                                    }`}
                                  />
                                </Button>
                              </div>
                            </div>
                          </MessageContent>
                        )}
                      </Message>
                    </MessageScrollerItem>
                  );
                })}

                {isStreaming && streamingContent && (
                  <MessageScrollerItem>
                    <Message align="start">
                      <MessageContent>
                        <UnifiedMarkdown
                          className="leading-5"
                          extraRehypePluginsAfterRaw={[
                            rehypeGroupSourceCitations,
                          ]}
                          components={reportCitationComponents}
                        >
                          {normalizeCitationMarkers(streamingContent)}
                        </UnifiedMarkdown>
                      </MessageContent>
                    </Message>
                  </MessageScrollerItem>
                )}
                {isFetchingLinks && !streamingContent && (
                  <MessageScrollerItem>
                    <Message align="start">
                      <MessageContent>
                        <div className="flex items-center gap-2">
                          <img
                            src={Loader}
                            alt="Loader"
                            height={16}
                            width={16}
                            className="dark:invert"
                          />
                          <span className="text-sm text-muted-foreground">
                            <FormattedMessage
                              id="chat.fetchingLinks"
                              values={{ count: linkFetchUrls.length }}
                            />
                          </span>
                        </div>
                      </MessageContent>
                    </Message>
                  </MessageScrollerItem>
                )}
                {isComprehensiveProcessing && !streamingContent && (
                  <MessageScrollerItem>
                    <Message align="start">
                      <MessageContent>
                        <div className="flex items-center gap-2">
                          <img
                            src={Loader}
                            alt="Loader"
                            height={16}
                            width={16}
                            className="dark:invert"
                          />
                          <span className="text-sm text-muted-foreground">
                            {comprehensiveStage || (
                              <FormattedMessage id="chat.comprehensiveProgress" />
                            )}
                          </span>
                        </div>
                      </MessageContent>
                    </Message>
                  </MessageScrollerItem>
                )}
                {isThinking &&
                  !isComprehensiveProcessing &&
                  !isFetchingLinks &&
                  !streamingContent && (
                    <MessageScrollerItem>
                      <Message align="start">
                        <MessageContent>
                          <div className="flex items-center gap-2">
                            <img
                              src={Loader}
                              alt="Loader"
                              height={16}
                              width={16}
                              className="dark:invert"
                            />
                            <span className="text-sm text-muted-foreground">
                              <FormattedMessage id="chat.thinking" />
                            </span>
                          </div>
                        </MessageContent>
                      </Message>
                    </MessageScrollerItem>
                  )}
                {(isLoading ||
                  (isMessageLoading &&
                    !streamingContent &&
                    !isThinking &&
                    !isComprehensiveProcessing &&
                    !isFetchingLinks)) && (
                  <MessageScrollerItem>
                    <Message align="start">
                      <MessageContent>
                        <img
                          src={Loader}
                          alt="Loader"
                          height={20}
                          width={20}
                          className="dark:invert"
                        />
                      </MessageContent>
                    </Message>
                  </MessageScrollerItem>
                )}
                {chatError && (
                  <MessageScrollerItem>
                    <span className="text-xs text-red-500">
                      <FormattedMessage
                        id="chat.error"
                        defaultMessage="Something went wrong. Please try again later."
                      />
                    </span>
                  </MessageScrollerItem>
                )}
              </MessageScrollerContent>
            </ReportContainerContext.Provider>
          </MessageScrollerViewport>
          <MessageScrollerButton />
        </MessageScroller>
      </MessageScrollerProvider>
      <p className="text-center text-[10px] text-muted-foreground py-1 whitespace-nowrap">
        <FormattedMessage id="aiDisclaimer" />
      </p>
      <div className="px-[12px] pb-[12px]">
        <ChatInput
          placeholder={formatMessage({ id: "chat.placeholder" })}
          onSend={handleSendMessage}
          disabled={isMessageLoading}
          isLoading={isMessageLoading}
        />
      </div>
      <TabControlBar className="inline-flex">
        <TabControlBar.Group className="gap-2 justify-center">
          <ClearChatButton disabled={isChatEmpty} />
          <ContentCopyButton
            content={chatMarkdownContent}
            disabled={isChatEmpty}
            eventName="app/doc_list/copy_all.button/click"
            preCopyMessageId="copy"
          />
          <MarkdownExportButton
            organizationId={organizationId}
            content={chatMarkdownContent}
            richContent={chatRichContent}
            disabled={isChatEmpty}
            fileName={exportFileName}
            buttonSize="mini"
            openEventName="app/doc_list/export.button/click"
            exportEventName="app/doc_list/export_menu_open/export_action.link/click"
            title={formatMessage({
              id: "common.chat",
            })}
            caseInfo={caseInfo}
          />
        </TabControlBar.Group>
      </TabControlBar>

      {pageTextModalOpen && (
        <MarkdownContentModal
          title={
            isKnowledgeBaseSource
              ? `${kbPageContentData?.filename ?? "Knowledge Base Document"} - Page ${(kbPageContentData?.pageNumber ?? 0) + 1}`
              : modalTitle
          }
          content={
            isKnowledgeBaseSource
              ? isKbPageContentLoading
                ? "Loading content..."
                : kbPageContentError
                  ? "Error loading content. This page may not have processed content available yet."
                  : kbPageContentData?.content ||
                    "No content available for this page."
              : isPageContentLoading || isContentLoading
                ? "Loading content..."
                : pageContentError
                  ? "Error loading content. This page may not have processed content available yet."
                  : pageContentData?.content ||
                    "No content available for this page."
          }
          filename={
            isKnowledgeBaseSource
              ? kbPageContentData?.filename
              : pageContentData?.filename
          }
          authorName={
            isKnowledgeBaseSource
              ? (kbPageContentData?.authorName ?? undefined)
              : pageContentData?.authorName
          }
          uploadDate={
            isKnowledgeBaseSource
              ? kbPageContentData?.uploadDate
              : pageContentData?.uploadDate
          }
          open={pageTextModalOpen}
          onOpenChange={(open) => {
            setPageTextModalOpen(open);
            if (!open) {
              // Reset KB source state when modal closes
              setIsKnowledgeBaseSource(false);
              setSelectedKbDocumentId(null);
              setSelectedKbPageNumber(null);
            }
          }}
          copyEventName="app/chat/page_text_modal/copy_all.button/click"
          exportOpenEventName="app/chat/page_text_modal/export.button/click"
          exportEventName="app/chat/page_text_modal/export_menu_open/export_action.link/click"
          terms={allTerms}
          pageImageUrl={
            isKnowledgeBaseSource
              ? (kbPageContentData?.scanUrl ?? undefined)
              : pageImageUrl
          }
          pageRotation={isKnowledgeBaseSource ? 0 : pageRotation}
        />
      )}
      <ImageLightbox
        src={lightboxImage ?? ""}
        alt={formatMessage({
          id: "chat.attachment.alt",
          defaultMessage: "Attached image",
        })}
        open={lightboxImage !== null}
        onOpenChange={(open) => {
          if (!open) setLightboxImage(null);
        }}
      />
    </div>
  );
};
